/** 工具方法 ::: 
 *  @function produceNumber 随机数方法
 */
import { produceNumber } from '@/utils/utils.js'

/** @name 转换 栅格列节点 数据
 *  @param {Object} formNode 表单项节点
 *  @param {Object} formMap 表单项字典
 *  @return {Object} newFormNode 当前版本标单项节点数据
 *  @author 王汝鹏
 *  @version 2019-12-25
 */
const conversionGridColumnFormNode = function(columns, formMap) {
  let { span, list } = columns;

  /** 栅格列组件节点 **/
  let gridColumnNode = {
    id: produceNumber(6),
    children: []
  }

  /** 栅格列组件节点配置 **/
  let gridColumnConfig = {
    node: {
      type: 'gridColumn',
      isCopied: false,
      copiedCount: 0,
      selfId: gridColumnNode.id
    },
    baseOption: { customName:"", span },
    styleOption: {}, renderOption: {},
    event: { emit:[], on:[] }
  }
  Reflect.set(formMap, gridColumnNode.id, gridColumnConfig);

  /** 处理栅格列子级节点 **/
  gridColumnNode.children = list.map(node => conversionFormNode(node, formMap));

  return gridColumnNode;
}


/** @name 转换 栅格表单项/节点 数据
 *  @param {Object} formNode 表单项节点
 *  @param {Object} formMap 表单项字典
 *  @return {Object} newFormNode 当前版本表单项节点数据
 *  @author 王汝鹏
 *  @version 2019-12-25
 */
const conversionGridFormNode = function(formNode, formMap) {
  let { type, name, node, baseOption, styleOption, event } = formNode;
  let { selfId } = node;
  let { columns } = baseOption;

  /** 栅格组件节点 **/
  let gridNode = {
    id: selfId,
    children: []
  };
  /** 删除栅格配置中的列记录，调整组件标题名称字段 **/
  Reflect.deleteProperty(baseOption, 'columns');
  Reflect.set(baseOption, 'labelName', name);
  /** 栅格组件配置 **/
  let gridConfig = {
    node: { type, ...node },
    renderOption: {},
    baseOption, styleOption, event
  }
  Reflect.set(formMap, selfId, gridConfig);

  /** 转换栅格节点的子级节点 **/
  gridNode.children = columns.map(node => conversionGridColumnFormNode(node, formMap));

  return gridNode;
}

/** @name 转换 任意门表单项/节点 数据
 *  @param {Object} formNode 表单项节点
 *  @param {Object} formMap 表单项字典 
 *  @return {Object} newFormNode 当前版本表单项节点数据
 *  @author 王汝鹏 
 *  @version 2019-12-25
 */
const conversionFreeFormNode = function(formNode, formMap) {
  let { type, name, node, baseOption, styleOption, event } = formNode;
  let { selfId } = node;
  let { list } = baseOption;
  
  /** 任意门组件节点 **/
  let freeNode = {
    id: selfId,
    children: []
  }

  /** 删除配置中的无用字段 **/
  Reflect.deleteProperty(baseOption, 'list');
  Reflect.set(baseOption, 'labelName', name);

  /** 任意门组件配置 **/
  let freeConfig = {
    node: { type, ...node },
    renderOption: {},
    baseOption, styleOption, event
  }
  Reflect.set(formMap, selfId, freeConfig);

  freeNode.children = list.map(node => conversionFormNode(node, formMap));

  return freeNode;
}


/** @name 转换 表单项/节点 数据
 *  @param {Object} formNode 表单项节点
 *  @param {Object} formMap 表单项字典
 *  @return {Object} newFormNode 当前版本表单项节点数据
 *  @author 王汝鹏
 *  @version 2019-12-25
 */
const conversionFormNode = function(formNode, formMap) {
  let { type, name, node, baseOption, styleOption, renderOption, event } = formNode;
  let { selfId } = node;

  if (type === 'grid') return conversionGridFormNode(formNode, formMap);
  if (type === 'free') return conversionFreeFormNode(formNode, formMap);

  /** 表单项节点 **/
  let newFormNode = {
    id: selfId,
    children: []
  };

  /** 标单项配置 **/
  let newFormConfig = {
    node: { type, ...node },
    baseOption: { labelName:name, ...baseOption },
    styleOption, renderOption, event
  };
  Reflect.set(formMap, selfId, newFormConfig);

  return newFormNode;
}

/** @name 转换模块组件数据
 *  @param {Object} domain 模块数据
 *  @param {Object} formMap 表单项字典
 *  @return {Object} newDomain 当前版本模块数据
 *  @author 王汝鹏 
 *  @version 2019-12-25
 */
const conversionDomain = function(domain, formMap) {
  let { type, name, icon, node, templates } = domain;
  let { selfId } = node;

  /** 转化后的组件配置 **/
  let newDomain = {
    exhibits: { type, name, icon },
    node: { id:selfId, children:[] },
    config: {
      node: { type, selfId },
      baseOption: {},
      styleOption: {},
      renderOption: {},
      event: { emit:[], on:[] }
    }
  }
  newDomain.node.children = templates.map(node => conversionFormNode(node, formMap));

  return newDomain;
}


/** @name 数据格式版本升级方法 0.0.5
 *  @param {Object} templateData 模版数据
 *  @return {Object} 0.0.5版本的模版数据格式
 *  @author 王汝鹏
 *  @version 2019-12-25
 */
export const upgrade = function(templateData={}) {
  /** 字段说明 ::: 
   *  - {String} version 版本号
   *  - {String} itemManage 扩展功能
   *  - {Array} domainList 模块组件列表
   *  - {Array} formList 拖拽区表单列表
   *  - {Object} formMap 标单项字典
   *  - {Object} eventMap 事件字典
   */
  let { version, itemManage='group', domainList=[], formList=[], eventMap={} } = templateData;

  // 如果结构已经是当前的版本，直接返回模版数据
  if (version === '0.0.5') return templateData;

  // 因为这里是处理的首个版本转化，所以不需要在这里判断是否为当前能够处理的版本
  // 如果是其它版本的话，需要使用上一版本的升级方法进行数据版本转化，然后等待返回该转化能够处理的版本在进行处理

  let newFormMap = {};
  let newEventMap = eventMap; 
  let newFormList = formList;
  let newDomainList = domainList;

  // 转换编辑区表单数据
  newFormList = newFormList.map(formNode => { return conversionFormNode(formNode, newFormMap); });

  // 转换模块组件列表数据
  newDomainList = newDomainList.map(domain => { return conversionDomain(domain, newFormMap); });

  // 处理事件配置
  for (let formId in newFormMap) {
    let formConfig = newFormMap[formId];
    let { emit=[], on=[] } = formConfig.event || {};
    /** 检查触发事件配置 **/
    for (let e of [...emit]) {
      let eventConfig = newEventMap[e] || {};
      let { emit=[] } = eventConfig;
      let index = emit.findIndex(({form}) => form === formId);
      // 如果事件配置中找不到当前表单项id，就从当前表单项事件配置中删除该事件
      if (index === -1) {
        let deleteIndex = emit.findIndex(eId => eId === e);
        emit.splice(deleteIndex, 1);
      }
    }
    /** 检查监听事件配置 **/
    for (let e of [...on]) {
      let eventConfig = newEventMap[e] || {};
      let { on=[] } = eventConfig;
      // 如果事件配置中找不到当前表单项id，就从当前表单项事件配置中删除该事件
      if (on.form !== formId) {
        let deleteIndex = on.findIndex(eId => eId === e);
        on.splice(deleteIndex, 1);
      }
    }
  }

  return {
    version: '0.0.5',
    itemManage,
    domainList: newDomainList,
    formList: newFormList,
    eventMap: newEventMap,
    formMap: newFormMap,
  }
}



// WEBPACK FOOTER //
// ./src/assets/version/v005.js